import React from 'react';
import { Link } from 'react-router-dom';
import AIAgent from './AIAgent';

const ToolsPage: React.FC = () => {
    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="text-center mb-12">
                    <h1 className="text-4xl font-bold text-gray-900 mb-4">
                        LL97 Analysis & Calculation Tools
                    </h1>
                    <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                        Comprehensive suite of tools for building energy analysis, 
                        LL97 compliance assessment, and retrofit planning.
                    </p>
                </div>

                {/* Analysis Tools Section */}
                <section className="mb-16">
                    <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
                        🔍 Building Analysis Tools
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        <Link
                            to="/building-analysis"
                            className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow border-l-4 border-primary-500"
                        >
                            <div className="flex items-center mb-4">
                                <div className="text-xl mr-4">🏢</div>
                                <div>
                                    <h3 className="text-xl font-semibold text-gray-900">Building Analysis</h3>
                                    <p className="text-sm text-gray-500">Comprehensive analysis</p>
                                </div>
                            </div>
                            <p className="text-gray-600 mb-4">
                                Detailed energy performance analysis, LL97 compliance assessment, 
                                and retrofit recommendations for NYC buildings.
                            </p>
                            <div className="text-sm text-gray-500 mb-4">
                                <p className="font-semibold mb-2">📋 How to Use:</p>
                                <ol className="list-decimal list-inside space-y-1">
                                    <li>Search by address or property name</li>
                                    <li>Review comprehensive energy data</li>
                                    <li>Check LL97 compliance status</li>
                                    <li>Explore retrofit recommendations</li>
                                    <li>Download detailed reports</li>
                                </ol>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                <span className="bg-primary-100 text-primary-800 text-xs px-2 py-1 rounded">Energy Audit</span>
                                <span className="bg-primary-100 text-primary-800 text-xs px-2 py-1 rounded">LL97 Status</span>
                                <span className="bg-primary-100 text-primary-800 text-xs px-2 py-1 rounded">Retrofits</span>
                            </div>
                        </Link>

                        <Link
                            to="/simple-view"
                            className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow border-l-4 border-green-500"
                        >
                            <div className="flex items-center mb-4">
                                <div className="text-3xl mr-4">📋</div>
                                <div>
                                    <h3 className="text-xl font-semibold text-gray-900">Simple Building View</h3>
                                    <p className="text-sm text-gray-500">Quick assessment</p>
                                </div>
                            </div>
                            <p className="text-gray-600 mb-4">
                                Streamlined interface for property managers to quickly assess 
                                building performance and compliance status.
                            </p>
                            <div className="text-sm text-gray-500 mb-4">
                                <p className="font-semibold mb-2">📋 How to Use:</p>
                                <ol className="list-decimal list-inside space-y-1">
                                    <li>Enter building address or name</li>
                                    <li>View simplified dashboard overview</li>
                                    <li>Check quick compliance status</li>
                                    <li>Access basic retrofit suggestions</li>
                                    <li>Export summary reports</li>
                                </ol>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded">Quick View</span>
                                <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded">Dashboard</span>
                                <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded">Reports</span>
                            </div>
                        </Link>

                        <Link
                            to="/calculation-verification"
                            className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow border-l-4 border-purple-500"
                        >
                            <div className="flex items-center mb-4">
                                <div className="text-3xl mr-4">✅</div>
                                <div>
                                    <h3 className="text-xl font-semibold text-gray-900">LL97 Verification</h3>
                                    <p className="text-sm text-gray-500">Compliance check</p>
                                </div>
                            </div>
                            <p className="text-gray-600 mb-4">
                                Verify LL97 compliance calculations and double-check 
                                fine assessments for accuracy.
                            </p>
                            <div className="text-sm text-gray-500 mb-4">
                                <p className="font-semibold mb-2">📋 How to Use:</p>
                                <ol className="list-decimal list-inside space-y-1">
                                    <li>Upload building energy data</li>
                                    <li>Enter emissions calculations</li>
                                    <li>Compare against LL97 limits</li>
                                    <li>Verify penalty calculations</li>
                                    <li>Get compliance recommendations</li>
                                </ol>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                <span className="bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded">Verification</span>
                                <span className="bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded">Accuracy</span>
                                <span className="bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded">Audit</span>
                            </div>
                        </Link>

                        <Link
                            to="/target-properties"
                            className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow border-l-4 border-orange-500"
                        >
                            <div className="flex items-center mb-4">
                                <div className="text-xl mr-4">🎯</div>
                                <div>
                                    <h3 className="text-xl font-semibold text-gray-900">Target Properties</h3>
                                    <p className="text-sm text-gray-500">Investment analysis</p>
                                </div>
                            </div>
                            <p className="text-gray-600 mb-4">
                                Identify high-value retrofit opportunities and analyze 
                                portfolio-level investment strategies.
                            </p>
                            <div className="text-sm text-gray-500 mb-4">
                                <p className="font-semibold mb-2">📋 How to Use:</p>
                                <ol className="list-decimal list-inside space-y-1">
                                    <li>Set investment criteria & budgets</li>
                                    <li>Filter by ROI and payback period</li>
                                    <li>Review portfolio opportunities</li>
                                    <li>Compare financial projections</li>
                                    <li>Export investment reports</li>
                                </ol>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                <span className="bg-orange-100 text-orange-800 text-xs px-2 py-1 rounded">Targeting</span>
                                <span className="bg-orange-100 text-orange-800 text-xs px-2 py-1 rounded">ROI</span>
                                <span className="bg-orange-100 text-orange-800 text-xs px-2 py-1 rounded">Portfolio</span>
                            </div>
                        </Link>

                        <Link
                            to="/non-compliant-buildings"
                            className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow border-l-4 border-red-500"
                        >
                            <div className="flex items-center mb-4">
                                <div className="text-xl mr-4">⚠️</div>
                                <div>
                                    <h3 className="text-xl font-semibold text-gray-900">Non-Compliant Directory</h3>
                                    <p className="text-sm text-gray-500">Risk assessment</p>
                                </div>
                            </div>
                            <p className="text-gray-600 mb-4">
                                Comprehensive directory of buildings at risk of LL97 
                                penalties with intervention opportunities.
                            </p>
                            <div className="text-sm text-gray-500 mb-4">
                                <p className="font-semibold mb-2">📋 How to Use:</p>
                                <ol className="list-decimal list-inside space-y-1">
                                    <li>Browse non-compliant buildings</li>
                                    <li>Filter by penalty amounts</li>
                                    <li>Identify intervention opportunities</li>
                                    <li>Contact building owners</li>
                                    <li>Track outreach progress</li>
                                </ol>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded">Non-Compliant</span>
                                <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded">Directory</span>
                                <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded">Risk</span>
                            </div>
                        </Link>
                    </div>
                </section>

                {/* Financial Calculators Section */}
                <section className="mb-16">
                    <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
                        🧮 Financial Calculators
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        <Link
                            to="/ll97-calculator"
                            className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow border-l-4 border-red-500"
                        >
                            <div className="flex items-center mb-4">
                                <div className="text-xl mr-4">💰</div>
                                <div>
                                    <h3 className="text-xl font-semibold text-gray-900">LL97 Fine Calculator</h3>
                                    <p className="text-sm text-gray-500">Penalty estimation</p>
                                </div>
                            </div>
                            <p className="text-gray-600 mb-4">
                                Calculate current and future LL97 fines based on building 
                                energy performance and compliance status.
                            </p>
                            <div className="text-sm text-gray-500 mb-4">
                                <p className="font-semibold mb-2">📋 How to Use:</p>
                                <ol className="list-decimal list-inside space-y-1">
                                    <li>Select building type & size</li>
                                    <li>Enter current emissions data</li>
                                    <li>View 2025 & 2030 penalty calculations</li>
                                    <li>Compare compliance scenarios</li>
                                    <li>Save calculation reports</li>
                                </ol>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded">LL97 Fines</span>
                                <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded">Penalties</span>
                                <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded">Cost</span>
                            </div>
                        </Link>

                        <Link
                            to="/retrofit-calculator"
                            className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow border-l-4 border-green-500"
                        >
                            <div className="flex items-center mb-4">
                                <div className="text-xl mr-4">🔧</div>
                                <div>
                                    <h3 className="text-xl font-semibold text-gray-900">Retrofit Calculator</h3>
                                    <p className="text-sm text-gray-500">Investment analysis</p>
                                </div>
                            </div>
                            <p className="text-gray-600 mb-4">
                                Analyze retrofit investment scenarios, ROI calculations, 
                                and energy savings projections.
                            </p>
                            <div className="text-sm text-gray-500 mb-4">
                                <p className="font-semibold mb-2">📋 How to Use:</p>
                                <ol className="list-decimal list-inside space-y-1">
                                    <li>Input building characteristics</li>
                                    <li>Select retrofit measures</li>
                                    <li>Review cost-benefit analysis</li>
                                    <li>Compare financing options</li>
                                    <li>Generate investment summary</li>
                                </ol>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded">ROI</span>
                                <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded">Savings</span>
                                <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded">Payback</span>
                            </div>
                        </Link>

                        <Link
                            to="/internal-retrofit-calculator"
                            className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow border-l-4 border-purple-500"
                        >
                            <div className="flex items-center mb-4">
                                <div className="text-xl mr-4">🔒</div>
                                <div>
                                    <h3 className="text-xl font-semibold text-gray-900">Professional Tools</h3>
                                    <p className="text-sm text-gray-500">Advanced analysis</p>
                                </div>
                            </div>
                            <p className="text-gray-600 mb-4">
                                Professional-grade retrofit analysis with advanced 
                                financial modeling and sensitivity analysis.
                            </p>
                            <div className="text-sm text-gray-500 mb-4">
                                <p className="font-semibold mb-2">📋 How to Use:</p>
                                <ol className="list-decimal list-inside space-y-1">
                                    <li>Access advanced modeling tools</li>
                                    <li>Run sensitivity analyses</li>
                                    <li>Compare multiple scenarios</li>
                                    <li>Generate professional reports</li>
                                    <li>Export detailed projections</li>
                                </ol>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                <span className="bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded">Professional</span>
                                <span className="bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded">Advanced</span>
                                <span className="bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded">Modeling</span>
                            </div>
                        </Link>
                    </div>
                </section>

                {/* Business Development Tools */}
                <section className="mb-16">
                    <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
                        🤝 Business Development Tools
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        <Link
                            to="/connection-hub"
                            className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow border-l-4 border-blue-500"
                        >
                            <div className="flex items-center mb-4">
                                <div className="text-3xl mr-4">🌐</div>
                                <div>
                                    <h3 className="text-xl font-semibold text-gray-900">Connection Hub</h3>
                                    <p className="text-sm text-gray-500">Partner network</p>
                                </div>
                            </div>
                            <p className="text-gray-600 mb-4">
                                Connect with vetted contractors, lenders, and service 
                                providers for your retrofit projects.
                            </p>
                            <div className="text-sm text-gray-500 mb-4">
                                <p className="font-semibold mb-2">📋 How to Use:</p>
                                <ol className="list-decimal list-inside space-y-1">
                                    <li>Browse verified service providers</li>
                                    <li>Filter by specialty & location</li>
                                    <li>Compare ratings & portfolios</li>
                                    <li>Request project quotes</li>
                                    <li>Manage partnership connections</li>
                                </ol>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">Network</span>
                                <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">Partners</span>
                                <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">Connect</span>
                            </div>
                        </Link>

                        <Link
                            to="/contractor-partnership-signup"
                            className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow border-l-4 border-green-500"
                        >
                            <div className="flex items-center mb-4">
                                <div className="text-3xl mr-4">🔨</div>
                                <div>
                                    <h3 className="text-xl font-semibold text-gray-900">Contractor Partnership</h3>
                                    <p className="text-sm text-gray-500">Service provider signup</p>
                                </div>
                            </div>
                            <p className="text-gray-600 mb-4">
                                Join our vetted contractor network and gain access to 
                                qualified retrofit projects across NYC.
                            </p>
                            <div className="text-sm text-gray-500 mb-4">
                                <p className="font-semibold mb-2">📋 How to Use:</p>
                                <ol className="list-decimal list-inside space-y-1">
                                    <li>Complete contractor application</li>
                                    <li>Submit credentials & portfolio</li>
                                    <li>Pass verification process</li>
                                    <li>Access project opportunities</li>
                                    <li>Build client relationships</li>
                                </ol>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded">Contractors</span>
                                <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded">Projects</span>
                                <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded">Growth</span>
                            </div>
                        </Link>

                        <Link
                            to="/lender-partnership-signup"
                            className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow border-l-4 border-purple-500"
                        >
                            <div className="flex items-center mb-4">
                                <div className="text-xl mr-4">🏦</div>
                                <div>
                                    <h3 className="text-xl font-semibold text-gray-900">Lender Partnership</h3>
                                    <p className="text-sm text-gray-500">Financial partnership</p>
                                </div>
                            </div>
                            <p className="text-gray-600 mb-4">
                                Partner with us to provide financing solutions for 
                                qualified energy retrofit projects.
                            </p>
                            <div className="text-sm text-gray-500 mb-4">
                                <p className="font-semibold mb-2">📋 How to Use:</p>
                                <ol className="list-decimal list-inside space-y-1">
                                    <li>Submit lender partnership application</li>
                                    <li>Review financing opportunities</li>
                                    <li>Access qualified project pipeline</li>
                                    <li>Offer CPACE & conventional loans</li>
                                    <li>Manage lending relationships</li>
                                </ol>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                <span className="bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded">Financing</span>
                                <span className="bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded">CPACE</span>
                                <span className="bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded">Loans</span>
                            </div>
                        </Link>
                    </div>
                </section>

                {/* Specialized Services */}
                <section className="mb-16">
                    <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
                        🎯 Specialized Services
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Link
                            to="/chinese-building-owners"
                            className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow border-l-4 border-red-500"
                        >
                            <div className="flex items-center mb-4">
                                <div className="text-3xl mr-4">🇨🇳</div>
                                <div>
                                    <h3 className="text-xl font-semibold text-gray-900">中文业主服务 | Chinese Owners</h3>
                                    <p className="text-sm text-gray-500">中英文服务 | Bilingual services</p>
                                </div>
                            </div>
                            <p className="text-gray-600 mb-4">
                                专为华人业主提供的LL97合规服务，包括中文咨询、技术支持和项目管理。
                                Comprehensive LL97 compliance services in Chinese and English.
                            </p>
                            <div className="text-sm text-gray-500 mb-4">
                                <p className="font-semibold mb-2">📋 使用方法 | How to Use:</p>
                                <ol className="list-decimal list-inside space-y-1">
                                    <li>中文咨询服务 | Chinese consultation</li>
                                    <li>LL97合规评估 | LL97 compliance assessment</li>
                                    <li>双语技术支持 | Bilingual technical support</li>
                                    <li>项目管理服务 | Project management services</li>
                                    <li>文档翻译协助 | Document translation assistance</li>
                                </ol>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded">中文服务</span>
                                <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded">华人业主</span>
                                <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded">双语支持</span>
                            </div>
                        </Link>

                        <Link
                            to="/definitions-tam"
                            className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow border-l-4 border-gray-500"
                        >
                            <div className="flex items-center mb-4">
                                <div className="text-3xl mr-4">📚</div>
                                <div>
                                    <h3 className="text-xl font-semibold text-gray-900">Definitions & Market Analysis</h3>
                                    <p className="text-sm text-gray-500">Knowledge base</p>
                                </div>
                            </div>
                            <p className="text-gray-600 mb-4">
                                Comprehensive definitions, market analysis, and total 
                                addressable market (TAM) calculations for the energy retrofit sector.
                            </p>
                            <div className="text-sm text-gray-500 mb-4">
                                <p className="font-semibold mb-2">📋 How to Use:</p>
                                <ol className="list-decimal list-inside space-y-1">
                                    <li>Browse industry definitions</li>
                                    <li>Review market size data</li>
                                    <li>Access TAM calculations</li>
                                    <li>Study sector trends</li>
                                    <li>Download market reports</li>
                                </ol>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                <span className="bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded">Definitions</span>
                                <span className="bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded">Market Data</span>
                                <span className="bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded">TAM</span>
                            </div>
                        </Link>
                    </div>
                </section>

                {/* Enhanced Quick Start Guide */}
                <section className="bg-gradient-to-r from-primary-600 to-primary-800 rounded-lg p-8 text-white text-center mb-16">
                    <h2 className="text-3xl font-bold mb-4">
                        Not Sure Where to Start?
                    </h2>
                    <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
                        Try our Building Analysis tool for a comprehensive assessment 
                        of your property's LL97 compliance status and retrofit opportunities.
                    </p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8 text-left">
                        <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                            <h3 className="font-bold text-lg mb-3 flex items-center">
                                <span className="mr-2">🏢</span> Building Owners
                            </h3>
                            <ul className="text-sm space-y-2">
                                <li>→ Start with Building Analysis</li>
                                <li>→ Check LL97 Fine Calculator</li>
                                <li>→ Explore Retrofit Calculator</li>
                                <li>→ Connect with service providers</li>
                            </ul>
                        </div>
                        
                        <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                            <h3 className="font-bold text-lg mb-3 flex items-center">
                                <span className="mr-2">💼</span> Service Providers
                            </h3>
                            <ul className="text-sm space-y-2">
                                <li>→ Browse Non-Compliant Directory</li>
                                <li>→ Join Partnership Programs</li>
                                <li>→ Use Target Properties tool</li>
                                <li>→ Access Professional Tools</li>
                            </ul>
                        </div>
                        
                        <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                            <h3 className="font-bold text-lg mb-3 flex items-center">
                                <span className="mr-2">📊</span> Investors & Analysts
                            </h3>
                            <ul className="text-sm space-y-2">
                                <li>→ Review Market Analysis</li>
                                <li>→ Use Target Properties tool</li>
                                <li>→ Access Professional Tools</li>
                                <li>→ Study TAM data</li>
                            </ul>
                        </div>
                    </div>
                    
                    <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-6">
                        <Link
                            to="/building-analysis"
                            className="bg-white text-primary-700 hover:bg-gray-100 font-bold py-3 px-8 rounded-lg text-lg transition-colors"
                        >
                            Start Building Analysis
                        </Link>
                        <Link
                            to="/contact"
                            className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-primary-700 font-bold py-3 px-8 rounded-lg text-lg transition-colors"
                        >
                            Get Expert Help
                        </Link>
                    </div>
                </section>

                {/* New Comprehensive Usage Guide */}
                <section className="mb-16">
                    <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
                        📖 Comprehensive Usage Guide
                    </h2>
                    
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-green-500">
                            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                                <span className="mr-2">🚀</span> Quick Start Workflow
                            </h3>
                            <ol className="list-decimal list-inside space-y-3 text-gray-700">
                                <li className="font-semibold">
                                    Assess Your Building
                                    <p className="text-sm font-normal text-gray-600 mt-1">
                                        Use Building Analysis or Simple View to understand current performance
                                    </p>
                                </li>
                                <li className="font-semibold">
                                    Calculate Financial Impact
                                    <p className="text-sm font-normal text-gray-600 mt-1">
                                        Run LL97 Fine Calculator to see potential penalties
                                    </p>
                                </li>
                                <li className="font-semibold">
                                    Explore Solutions
                                    <p className="text-sm font-normal text-gray-600 mt-1">
                                        Use Retrofit Calculator to compare investment options
                                    </p>
                                </li>
                                <li className="font-semibold">
                                    Connect with Experts
                                    <p className="text-sm font-normal text-gray-600 mt-1">
                                        Browse Connection Hub for qualified service providers
                                    </p>
                                </li>
                            </ol>
                        </div>
                        
                        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-blue-500">
                            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                                <span className="mr-2">💡</span> Pro Tips for Success
                            </h3>
                            <ul className="space-y-3 text-gray-700">
                                <li className="flex items-start">
                                    <span className="text-blue-500 mr-2 mt-1">•</span>
                                    <div>
                                        <span className="font-semibold">Start Early:</span>
                                        <span className="text-sm text-gray-600 block">Begin analysis 2-3 years before compliance deadlines</span>
                                    </div>
                                </li>
                                <li className="flex items-start">
                                    <span className="text-blue-500 mr-2 mt-1">•</span>
                                    <div>
                                        <span className="font-semibold">Use Multiple Tools:</span>
                                        <span className="text-sm text-gray-600 block">Cross-reference results across different calculators</span>
                                    </div>
                                </li>
                                <li className="flex items-start">
                                    <span className="text-blue-500 mr-2 mt-1">•</span>
                                    <div>
                                        <span className="font-semibold">Verify Data:</span>
                                        <span className="text-sm text-gray-600 block">Use LL97 Verification tool to double-check calculations</span>
                                    </div>
                                </li>
                                <li className="flex items-start">
                                    <span className="text-blue-500 mr-2 mt-1">•</span>
                                    <div>
                                        <span className="font-semibold">Get Professional Help:</span>
                                        <span className="text-sm text-gray-600 block">Contact experts for complex projects or large portfolios</span>
                                    </div>
                                </li>
                            </ul>
                        </div>
                    </div>
                </section>
            </div>
            
            {/* AI Agent Integration */}
            <AIAgent 
                context="tools" 
                currentPage="tools"
            />
        </div>
    );
};

export default ToolsPage;
